import { memo, useCallback, useMemo, useState } from 'react'
import { ButtonBase, ClickAwayListener } from '@mui/material'
import { getCountryCallingCode } from 'libphonenumber-js'
import { useTranslations } from '@/core/translation'
import { Popper } from '@/components/Surface'
import { Box } from '@/components/Layout'
import { DropdownList } from '@/components/Data'
import Input from '../../Input'
import { phoneNumberCountries } from './PhoneNumberCountryDropdown.data'
import { PhoneNumberCountryDropdownProps } from './PhoneNumberCountryDropdown.types'
import { phoneNumberCountryDropdownStyles as styles } from './PhoneNumberCountryDropdown.styles'

function PhoneNumberCountryDropdown({
  inputRef,
  selectedOption,
  setSelectedOption,
}: PhoneNumberCountryDropdownProps) {
  const t = useTranslations('components')

  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const handleToggle = useCallback(() => setOpen((prev) => !prev), [])
  const handleClose = useCallback(() => setOpen(false), [])

  const filteredOptions = useMemo(
    () =>
      phoneNumberCountries.filter((option) => {
        const label = option.label.toLowerCase()
        const searchValueLower = searchValue.toLowerCase()
        return label.includes(searchValueLower)
      }),
    [searchValue],
  )

  return (
    <>
      <ButtonBase onClick={handleToggle} disableRipple disableTouchRipple sx={styles.button}>
        {selectedOption
          ? `+${getCountryCallingCode(selectedOption.value)}`
          : t('phoneNumberInput.select')}
      </ButtonBase>

      <Popper
        open={open}
        anchorEl={inputRef.current}
        placement="bottom"
        parentWidth
        sx={styles.popper}>
        <ClickAwayListener onClickAway={handleClose}>
          <Box sx={styles.dropdownWrapper}>
            <Box sx={styles.inputWrapper}>
              <Input
                value={searchValue}
                labelPlacement="outside"
                placeholder={t('phoneNumberInput.search')}
                onChange={(event) => setSearchValue(event.target.value)}
              />
            </Box>
            <DropdownList
              options={filteredOptions}
              idKey="value"
              labelKey="label"
              valueKey="value"
              value={selectedOption.value}
              onChange={(option) => {
                setSelectedOption(option)
                handleClose()
              }}
            />
          </Box>
        </ClickAwayListener>
      </Popper>
    </>
  )
}

export default memo(PhoneNumberCountryDropdown)
